import {css, StyleSheet} from 'aphrodite';
import {Kind, ArgumentNode} from 'query-api-graphql';
import React, {useCallback} from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {List} from 'data-types/benchling-type-def/ComplexTypes';
import BenchlingTypeValueInput from 'data-types/components/BenchlingTypeValueInput';
import GraphQLFieldSelectorWrapper from 'pipelines/query-api-step-config/GraphQLFieldSelectorWrapper';
import QueryApiAllSchemaSelector, {
  QueryApiSchemaSelectorSchema,
} from 'pipelines/query-api-step-config/QueryApiAllSchemaSelector';
import {
  DEFAULT_FIELD_SELECTION,
  PLATE_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT,
} from 'pipelines/query-api-step-config/SchemaDatasetCreationConstants';
import {
  assertHasArchiveReasonArg,
  getFieldDisplayNameFn,
  typeHasDefaultField,
} from 'pipelines/query-api-step-config/SchemaDatasetCreationUtils';
import {buildAnyOfFilterValue} from 'query-api/QueryApiFilters';
import {QueryApiIntrospection} from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {stepperStyles, StepperV2} from 'ui-library/stepper/Stepper';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';

import {
  ColumnMappingStepperStepProps,
  getColumnMappingStepperStepParams,
} from '../column-mapping/ColumnMappingStepperStep';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import DataFrameNameField from '../steps/DataFrameNameField';
import {FormState} from './RegistrySchemaDataFrameCreationTypes';

// Capped for performance reasons, we can do performance testing if we need to raise it.
export const MAX_SELECTED_PLATE_IDS = 10;

function plateIdsToArgumentNode(plateIds: Array<string>): ArgumentNode {
  return {
    kind: Kind.ARGUMENT,
    name: {
      kind: Kind.NAME,
      value: 'id',
    },
    value: buildAnyOfFilterValue(plateIds),
  };
}

interface PlateSchemaStepperFormProps {
  dataFrameNameState: DataFrameNameState;
  setDataFrameNameState: (state: DataFrameNameState) => void;
  setQueryFormState: (state: FormState) => void;
  queryApiIntrospection: QueryApiIntrospection;
  queryFormState: FormState;
  selectedPlateIds: Array<string>;
  setSelectedPlateIds: (plateIds: Array<string>) => void;
  columnMappingProps?: ColumnMappingStepperStepProps;
  disabled?: boolean;
}

function PlateSchemaStepperForm({
  columnMappingProps,
  dataFrameNameState,
  setDataFrameNameState,
  setQueryFormState,
  queryApiIntrospection,
  queryFormState,
  selectedPlateIds,
  setSelectedPlateIds,
  disabled,
}: PlateSchemaStepperFormProps): JSX.Element {
  const queryListingField =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryField(queryFormState.entityListingFieldName);

  const schemaId =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryFieldSchemaId(queryFormState.entityListingFieldName);

  const handleChangeSchema = useCallback(
    (schema: QueryApiSchemaSelectorSchema | null): void => {
      if (schema == null) {
        setQueryFormState({entityListingFieldName: null});
        return;
      }

      const listingField = queryApiIntrospection.getQueryListingFieldForSchema(schema.id);
      assert(listingField != null, 'User selected a schema without a corresponding Query API listing field');

      const errorLoggingData = {
        schemaId: schema.id,
        schemaName: schema.name,
        listingFieldName: listingField.name,
      };

      assertHasArchiveReasonArg(listingField, errorLoggingData);

      setSelectedPlateIds([]);
      setQueryFormState({
        entityListingFieldName: listingField.name,
        entityFilterArguments: [plateIdsToArgumentNode([])],
        entityFieldSelections: {
          kind: Kind.SELECTION_SET,
          selections: typeHasDefaultField(listingField.type) ? [DEFAULT_FIELD_SELECTION] : [],
        },
        relatedResultsFormStates: [],
      });

      if (!dataFrameNameState.hasUserEdits) {
        setDataFrameNameState({...dataFrameNameState, name: `${schema.name} data`});
      }
    },
    [queryApiIntrospection, setQueryFormState, dataFrameNameState, setDataFrameNameState, setSelectedPlateIds]
  );

  return (
    <StepperV2
      steps={[
        {
          title: <div className={css(stepperStyles.defaultStepTitle)}>Select plate schema</div>,
          content: (
            <div className={css(stepperStyles.defaultStepContent)}>
              <QueryApiAllSchemaSelector
                schemaId={schemaId}
                handleSchemaChange={handleChangeSchema}
                queryApiIntrospection={queryApiIntrospection}
                schemaFilter={(schema) => APIUtil.isPlateSchema(schema.id)}
                disabled={disabled}
              />
            </div>
          ),
          key: 'plate-schema-selector',
        },
        schemaId != null &&
          queryFormState.entityListingFieldName != null && {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Select plates</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                {selectedPlateIds.length > MAX_SELECTED_PLATE_IDS && (
                  <div className={css(styles.bannerWrapper)}>
                    <InlineBanner
                      bannerType={InlineBannerType.ERROR}
                      text={`At most ${MAX_SELECTED_PLATE_IDS} plates can be selected.`}
                    />
                  </div>
                )}
                <BenchlingTypeValueInput
                  typeDef={new List(LeafType.PLATE)}
                  allowAutoFocus={false}
                  onChange={(newSelection) => {
                    if (newSelection == null) {
                      setSelectedPlateIds([]);
                    } else {
                      assertAndContinue(Array.isArray(newSelection), 'Expected an array of plate IDs');
                      const newSelectedPlateIds = _.unique(newSelection as Array<string>);
                      const plateIdsArg = plateIdsToArgumentNode(newSelectedPlateIds);

                      setQueryFormState({
                        ...queryFormState,
                        entityFilterArguments: [plateIdsArg],
                      });

                      setSelectedPlateIds(_.unique(newSelection as Array<string>));
                    }
                  }}
                  value={selectedPlateIds}
                  schemaId={schemaId}
                  hideSelectedResults={true}
                  disabled={disabled}
                />
              </div>
            ),
            key: 'plates-selector',
          },
        schemaId != null &&
          queryFormState.entityListingFieldName != null &&
          queryListingField != null && {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Select fields</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                <GraphQLFieldSelectorWrapper
                  field={queryListingField}
                  logicalDepthLimit={PLATE_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT}
                  selectionSet={queryFormState.entityFieldSelections}
                  setSelectionSet={(newSelectionSet) => {
                    setQueryFormState({...queryFormState, entityFieldSelections: newSelectionSet});
                  }}
                  getFieldDisplayName={getFieldDisplayNameFn(queryListingField, queryApiIntrospection)}
                  {...pick(
                    queryApiIntrospection,
                    'getTypeDisplayName',
                    'graphQLSchema',
                    'isDeprecatedField',
                    'isBacklinkField',
                    'isUserDefinedField',
                    'isUserComputedField'
                  )}
                />
              </div>
            ),
            key: 'fields-selector',
          },
        columnMappingProps != null &&
          schemaId != null &&
          queryFormState.entityListingFieldName != null &&
          queryListingField != null &&
          getColumnMappingStepperStepParams({...columnMappingProps, key: 'column-mapping-step'}),
        schemaId != null &&
          queryFormState.entityListingFieldName != null &&
          queryListingField != null && {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Name table</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                <DataFrameNameField
                  {...dataFrameNameState}
                  onNameChange={(newName: string) => {
                    setDataFrameNameState({name: newName, hasUserEdits: true});
                  }}
                  disabled={disabled}
                />
              </div>
            ),
            key: 'name-table',
          },
      ]}
    />
  );
}

export default React.memo(PlateSchemaStepperForm);

const styles = StyleSheet.create({
  bannerWrapper: {
    marginBottom: 5,
  },
});
